import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { Star, ChevronLeft, ChevronRight, Quote } from "lucide-react";

const TESTIMONIALS = [
  {
    name: "Marcus Holloway",
    company: "Apex Roofing Co.",
    role: "Founder & CEO",
    quote:
      "We tripled inbound leads in 4 months. EchoWings rebuilt our brand, site and SEO from scratch — the results compound every month.",
    avatar:
      "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBoZWFkc2hvdCUyMHNtaWxpbmd8ZW58MHx8fHwxNzgyNTg3Mjk2fDA&ixlib=rb-4.1.0&q=85",
  },
  {
    name: "Priya Sharma",
    company: "Northbridge Clinic",
    role: "Director of Marketing",
    quote:
      "The most strategic agency we've ever worked with. We rank #1 for every search that matters and ChatGPT now cites us by name.",
    avatar:
      "https://images.unsplash.com/photo-1494790108377-be9c29b29330?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBoZWFkc2hvdCUyMHNtaWxpbmd8ZW58MHx8fHwxNzgyNTg3Mjk2fDA&ixlib=rb-4.1.0&q=85",
  },
  {
    name: "David Okafor",
    company: "Driftboard SaaS",
    role: "Co-Founder",
    quote:
      "From generic startup look to a category-leader brand in 8 weeks. Our paid CAC dropped 38% just from the new site alone.",
    avatar:
      "https://images.pexels.com/photos/12311567/pexels-photo-12311567.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    name: "Elena Russo",
    company: "Maison Hospitality",
    role: "Brand Director",
    quote:
      "The new site converts at 4.2% — up from 0.9%. Bookings are up 218% YoY and we finally look as premium as we feel.",
    avatar:
      "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&q=80&w=800",
  },
  {
    name: "Jordan Reyes",
    company: "Ironcore Build Co.",
    role: "Operations Lead",
    quote:
      "EchoWings runs our entire growth engine. Calls are up 240%, and we close 2× more deals because the brand finally signals quality.",
    avatar:
      "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&q=80&w=800",
  },
  {
    name: "Sofia Lin",
    company: "Lumen Apparel",
    role: "E-commerce Lead",
    quote:
      "Revenue up 186% in two quarters. The team treats our brand like their own — feels like an in-house team, not a vendor.",
    avatar:
      "https://images.unsplash.com/photo-1531123897727-8f129e1688ce?auto=format&fit=crop&q=80&w=800",
  },
];

const CARD_VARIANTS = {
  enter: (dir) => ({ x: dir > 0 ? 60 : -60, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (dir) => ({ x: dir > 0 ? -60 : 60, opacity: 0 }),
};

export default function Testimonials() {
  const [page, setPage] = useState(0);
  const [dir, setDir] = useState(0);

  // Show 3 per page on desktop, 2 on tablet, 1 on mobile — but we paginate by 3 (visual grid)
  const PER_PAGE = 3;
  const pages = useMemo(() => {
    const out = [];
    for (let i = 0; i < TESTIMONIALS.length; i += PER_PAGE) {
      out.push(TESTIMONIALS.slice(i, i + PER_PAGE));
    }
    return out;
  }, []);
  const totalPages = pages.length;

  const go = (d) => {
    setDir(d);
    setPage((p) => (p + d + totalPages) % totalPages);
  };

  const current = pages[page];

  return (
    <section
      data-testid="testimonials"
      className="section relative bg-gradient-to-b from-transparent via-white/[0.015] to-transparent overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-300 text-xs font-semibold tracking-widest uppercase">
              Testimonials
            </div>
            <h2 className="mt-5 font-display text-4xl md:text-5xl font-bold tracking-tight">
              Trusted by ambitious operators.
            </h2>
          </div>

          <div className="flex items-center gap-3">
            <div className="text-sm text-white/45 font-medium" data-testid="testimonials-counter">
              <span className="text-white">{String(page + 1).padStart(2, "0")}</span>
              <span className="mx-1.5 text-white/30">/</span>
              <span>{String(totalPages).padStart(2, "0")}</span>
            </div>
            <button
              data-testid="testimonials-prev"
              onClick={() => go(-1)}
              className="w-11 h-11 rounded-full bg-white/5 border border-white/10 flex items-center justify-center hover:bg-white/10 hover:border-cyan-500/40 transition-all"
              aria-label="Previous testimonials"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              data-testid="testimonials-next"
              onClick={() => go(1)}
              className="w-11 h-11 rounded-full bg-gradient-to-r from-blue-600 to-cyan-400 text-black flex items-center justify-center hover:shadow-[0_0_20px_rgba(0,229,255,0.4)] transition-all"
              aria-label="Next testimonials"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="relative">
          <motion.div
            key={page}
            custom={dir}
            variants={CARD_VARIANTS}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5"
            data-testid="testimonials-grid"
          >
            {current.map((t, i) => (
              <article
                key={`${page}-${t.name}`}
                data-testid={`testimonial-card-${page}-${i}`}
                className="group relative rounded-3xl border border-white/10 bg-[#0a0a0a] p-7 hover:border-cyan-500/30 transition-colors duration-500 flex flex-col"
              >
                <Quote className="absolute top-6 right-6 w-9 h-9 text-cyan-500/10" />

                <div className="flex items-center gap-1">
                  {[...Array(5)].map((_, k) => (
                    <Star key={k} className="w-3.5 h-3.5 fill-cyan-400 text-cyan-400" />
                  ))}
                </div>

                <p className="mt-5 text-white/85 leading-relaxed flex-1">
                  &ldquo;{t.quote}&rdquo;
                </p>

                <div className="mt-7 pt-6 border-t border-white/5 flex items-center gap-3">
                  <img
                    src={t.avatar}
                    alt={t.name}
                    className="w-11 h-11 rounded-full object-cover border border-white/10"
                    loading="lazy"
                  />
                  <div>
                    <div className="font-semibold text-white text-sm">
                      {t.name}
                    </div>
                    <div className="text-xs text-white/50">
                      {t.role} · {t.company}
                    </div>
                  </div>
                </div>
              </article>
            ))}
            {/* Fill empty slots when last page has < PER_PAGE items so grid stays aligned */}
            {Array.from({ length: PER_PAGE - current.length }).map((_, i) => (
              <div key={`empty-${i}`} className="hidden md:block" />
            ))}
          </motion.div>
        </div>

        <div className="mt-10 flex justify-center gap-2">
          {pages.map((_, k) => (
            <button
              key={k}
              data-testid={`testimonials-dot-${k}`}
              onClick={() => {
                setDir(k > page ? 1 : -1);
                setPage(k);
              }}
              className={`h-2 rounded-full transition-all ${
                k === page ? "w-8 bg-cyan-400" : "w-2 bg-white/20 hover:bg-white/40"
              }`}
              aria-label={`Page ${k + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
